// src/components/activity/ActivityDetailsModal.jsx
import React, { useState, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

const INTENSITY_OPTIONS = [
  { value: "Low", labelKey: "modal.intensity.low" },
  { value: "Medium", labelKey: "modal.intensity.medium" },
  { value: "High", labelKey: "modal.intensity.high" },
  { value: "Max", labelKey: "modal.intensity.max" },
];

const SPORT_OPTIONS = [
  { value: "running", labelKey: "modal.sport.running" },
  { value: "cycling", labelKey: "modal.sport.cycling" },
  { value: "swimming", labelKey: "modal.sport.swimming" },
  { value: "lifting", labelKey: "modal.sport.lifting" },
  { value: "team_sport", labelKey: "modal.sport.team_sport" },
  { value: "other", labelKey: "modal.sport.other" },
];

// --- presentational shell that assumes it's already portalled ---
function ActivityDetailsModalInner({
  isVisible,
  onSave,
  onSkip,
  initialData,
  defaultTitle = "Exertion Recovery Test",
}) {
  const { t } = useTranslation();

  // === LOCK BACKGROUND SCROLL WHEN VISIBLE ===
  useEffect(() => {
    if (!isVisible) return;

    const appScroll = document.getElementById("app-scroll");
    const html = document.documentElement;
    const body = document.body;

    const prevAppOverflow = appScroll?.style.overflow;
    const prevAppTouch = appScroll?.style.touchAction;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;

    if (appScroll) {
      appScroll.style.overflow = "hidden";
      appScroll.style.touchAction = "none";
    }
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";

    return () => {
      if (appScroll) {
        appScroll.style.overflow = prevAppOverflow || "";
        appScroll.style.touchAction = prevAppTouch || "";
      }
      html.style.overflow = prevHtmlOverflow || "";
      body.style.overflow = prevBodyOverflow || "";
    };
  }, [isVisible]);

  // --- derive initial duration h/m from duration_min ---
  const getInitialDurationParts = (data) => {
    const total =
      typeof data?.duration_min === "number"
        ? data.duration_min
        : data?.duration_min
        ? Number(data.duration_min)
        : 0;

    if (!Number.isFinite(total) || total <= 0) {
      return { hours: 0, minutes: 0 };
    }
    const hours = Math.floor(total / 60);
    const minutes = total % 60;
    return { hours, minutes };
  };

  const durationParts = getInitialDurationParts(initialData);

  const [title, setTitle] = useState(initialData?.title || defaultTitle || "");
  const [exercise, setExercise] = useState(initialData?.exercise || "");
  const [sport, setSport] = useState(initialData?.sport || "other");
  const [durationHours, setDurationHours] = useState(durationParts.hours);
  const [durationMinutes, setDurationMinutes] = useState(durationParts.minutes);
  const [intensity, setIntensity] = useState(
    initialData?.intensity || "Medium"
  );
  const [description, setDescription] = useState(
    initialData?.description || ""
  );

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  // Keep local state in sync when editing existing details
  useEffect(() => {
    if (!initialData) return;

    setTitle(initialData.title || defaultTitle || "");
    setExercise(initialData.exercise || "");
    setSport(initialData.sport || "other");
    setIntensity(initialData.intensity || "Medium");
    setDescription(initialData.description || "");

    const parts = getInitialDurationParts(initialData);
    setDurationHours(parts.hours);
    setDurationMinutes(parts.minutes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialData?.title,
    initialData?.exercise,
    initialData?.duration_min,
    initialData?.sport,
    initialData?.intensity,
    initialData?.description,
  ]);

  const totalDurationMin =
    (Number(durationHours) || 0) * 60 + (Number(durationMinutes) || 0);

  const handleSave = useCallback(async () => {
    if (isSaving) return;
    setError("");

    if (!exercise || !intensity || totalDurationMin <= 0) {
      setError(
        t("modal.error.required", {
          defaultValue:
            "Please fill in all required fields and set a non-zero duration.",
        })
      );
      return;
    }

    const payload = {
      title,
      exercise,
      sport,
      duration_min: totalDurationMin,
      intensity,
      description: description?.trim() || undefined,
      activity_details_logged: true,
    };

    setIsSaving(true);
    try {
      await onSave(payload);
    } catch (e) {
      setError(
        t("modal.error.saveFailed", {
          defaultValue: "Could not save details. Please try again.",
        })
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    title,
    exercise,
    sport,
    totalDurationMin,
    intensity,
    description,
    isSaving,
    onSave,
    t,
  ]);

  const modalClass = isVisible
    ? "translate-y-0 opacity-100"
    : "translate-y-full opacity-0 pointer-events-none";

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-end justify-center transition-colors duration-300 ${
        isVisible ? "bg-black/50" : "bg-transparent pointer-events-none"
      }`}
    >
      <div
        className={`w-full md:max-w-lg mx-auto bg-container text-greyscale-1 rounded-t-2xl shadow-2xl pt-4 pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)] px-6 transform transition-transform duration-500 ease-out ${modalClass} overflow-hidden flex flex-col`}
        style={{ maxHeight: "80vh" }}
      >
        {/* Scrollable content area */}
        <div className="flex-1 flex flex-col gap-1 overflow-y-auto pr-1">
          <h2 className="subtitle-text text-primary mb-2">
            {t("modal.title", {
              defaultValue: "Log Your Pre-Test Activity",
            })}
          </h2>

          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => e.preventDefault()}
          >
            {/* 0. Title (optional) */}
            <label className="flex flex-col gap-1">
              <span>
                {t("modal.titleLabel", {
                  defaultValue: "Title (optional)",
                })}
              </span>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="custom-input-on-dark"
                placeholder={t("modal.titlePlaceholder", {
                  defaultValue: "e.g., Evening intervals",
                })}
              />
            </label>

            {/* 1. Sport */}
            <label className="flex flex-col gap-1">
              <span>{t("modal.sportLabel", { defaultValue: "Sport" })}</span>
              <select
                value={sport}
                onChange={(e) => setSport(e.target.value)}
                className="custom-input-on-dark appearance-none"
              >
                {SPORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {t(opt.labelKey, { defaultValue: opt.value })}
                  </option>
                ))}
              </select>
            </label>

            {/* 2. Exercise */}
            <label className="flex flex-col gap-1">
              <span>
                {t("modal.exerciseLabel", {
                  defaultValue: "Exercise (e.g., Interval Training)",
                })}
                <span className="text-red-500">*</span>
              </span>
              <input
                type="text"
                value={exercise}
                onChange={(e) => setExercise(e.target.value)}
                className="custom-input-on-dark"
                placeholder={t("modal.exercisePlaceholder", {
                  defaultValue: "e.g., 5km run",
                })}
                required
              />
            </label>

            {/* 3. Description (textarea) */}
            <label className="flex flex-col gap-1">
              <span>
                {t("modal.descriptionLabel", {
                  defaultValue: "Description (optional)",
                })}
              </span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="custom-input-on-dark min-h-[80px] resize-vertical"
                placeholder={t("modal.descriptionPlaceholder", {
                  defaultValue:
                    "e.g., Warm conditions, felt strong in the first half but faded towards the end.",
                })}
              />
            </label>

            {/* 4. Duration HH:MM */}
            <label className="flex flex-col gap-1">
              <span>
                {t("modal.durationLabel", {
                  defaultValue: "Duration",
                })}
                <span className="text-red-500">*</span>
              </span>
              <div className="flex gap-3 ">
                <div className="flex-1 flex flex-col gap-1">
                  <span className="text-xs text-greyscale-1">
                    {t("modal.duration.hours", { defaultValue: "Hours" })}
                  </span>
                  <select
                    value={durationHours}
                    onChange={(e) => setDurationHours(e.target.value)}
                    className="custom-input-on-dark appearance-none text-center h-10 py-1 leading-tight"
                  >
                    {Array.from({ length: 10 }).map((_, h) => (
                      <option key={h} value={h}>
                        {h.toString().padStart(2, "0")}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex-1 flex flex-col gap-1">
                  <span className="text-xs text-greyscale-1">
                    {t("modal.duration.minutes", { defaultValue: "Minutes" })}
                  </span>
                  <select
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(e.target.value)}
                    className="custom-input-on-dark appearance-none text-center h-10 py-1 leading-tight"
                  >
                    {Array.from({ length: 12 }).map((_, idx) => {
                      const m = idx * 5;
                      return (
                        <option key={m} value={m}>
                          {m.toString().padStart(2, "0")}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              {totalDurationMin > 0 && (
                <span className="text-xs text-greyscale-1 mt-1">
                  {t("modal.durationSummary", {
                    defaultValue: "{{minutes}} min total",
                    minutes: totalDurationMin,
                  })}
                </span>
              )}
            </label>

            {/* 5. Intensity pills */}
            <div className="flex flex-col gap-2">
              <span>
                {t("modal.intensityLabel", { defaultValue: "Intensity" })}
                <span className="text-red-500">*</span>
              </span>
              <div className="flex gap-2 flex-wrap">
                {INTENSITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setIntensity(opt.value)}
                    className={`px-4 py-2 text-sm rounded-lg transition-colors border-2 ${
                      intensity === opt.value
                        ? "bg-primary text-greyscale-1 shadow-md border-primary"
                        : "bg-transparent text-primary border-primary hover:bg-primary/10"
                    }`}
                  >
                    {t(opt.labelKey, { defaultValue: opt.value })}
                  </button>
                ))}
              </div>
            </div>

            {/* Error */}
            {error && (
              <p className="text-sm text-red-500 p-2 border border-red-500 rounded-lg bg-red-50/50">
                {error}
              </p>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-2 pb-1">
              <button
                type="button"
                onClick={onSkip}
                className={`outlined-button ${
                  isSaving ? "opacity-60 cursor-not-allowed" : ""
                }`}
                disabled={isSaving}
              >
                {t("modal.skip", { defaultValue: "Skip for now" })}
              </button>
              <button
                type="submit"
                onClick={handleSave}
                className={`primary-button ${
                  isSaving ? "opacity-60 cursor-not-allowed" : ""
                }`}
                disabled={isSaving}
              >
                {isSaving
                  ? t("modal.saving", { defaultValue: "Saving..." })
                  : t("modal.save", { defaultValue: "Save Details" })}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// --- exported wrapper that portals to <body> ---
export default function ActivityDetailsModal(props) {
  if (typeof document === "undefined") return null;

  const target = document.getElementById("modal-root") || document.body;

  return ReactDOM.createPortal(
    <ActivityDetailsModalInner {...props} />,
    target
  );
}
